/**
 * Product Reviews Tabs - Tab switching, slider scroll, dot sync
 * Vanilla JS, no external deps. Section-scoped.
 */
(function () {
  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.productReviewsTabsInit) return;
    sectionEl.dataset.productReviewsTabsInit = '1';

    const chipList = sectionEl.querySelector('.product-reviews-tabs__chip-list');
    const chips = sectionEl.querySelectorAll('[role="tab"]');
    const panels = sectionEl.querySelectorAll('.product-reviews-tabs__panel');

    const modalBackdrop = sectionEl.querySelector('[data-review-modal-backdrop]');
    const modalCloseBtn = sectionEl.querySelector('[data-review-modal-close]');
    const modalEl = modalBackdrop ? modalBackdrop.querySelector('.product-reviews-tabs__modal') : null;
    const modalNameEl = modalBackdrop ? modalBackdrop.querySelector('[data-modal-reviewer-name]') : null;
    const modalTitleEl = modalBackdrop ? modalBackdrop.querySelector('[data-modal-reviewer-title]') : null;
    const modalTextEl = modalBackdrop ? modalBackdrop.querySelector('[data-modal-review-text]') : null;
    const modalImageWrapper = modalBackdrop ? modalBackdrop.querySelector('[data-modal-reviewer-image-wrapper]') : null;
    const modalImageEl = modalBackdrop ? modalBackdrop.querySelector('[data-modal-reviewer-image]') : null;

    var isModalOpen = false;

    function openModalFromButton(btn) {
      if (!modalBackdrop || !modalEl || !modalTextEl) return;
      var name = btn.getAttribute('data-reviewer-name') || '';
      var title = btn.getAttribute('data-reviewer-title') || '';
      var reviewHtml = btn.getAttribute('data-review-text-html') || '';
      var imageSrc = btn.getAttribute('data-reviewer-image-src') || '';
      var imageRadius = btn.getAttribute('data-reviewer-image-radius') || '';

      if (modalNameEl) modalNameEl.textContent = name;
      if (modalTitleEl) modalTitleEl.textContent = title;
      if (modalTextEl) modalTextEl.innerHTML = reviewHtml;

      if (modalImageWrapper && modalImageEl) {
        if (imageSrc) {
          modalImageEl.src = imageSrc;
          modalImageEl.alt = name || '';
          if (imageRadius) {
            modalImageEl.style.setProperty('--reviewer-image-radius', imageRadius + '%');
          }
          modalImageWrapper.hidden = false;
        } else {
          modalImageWrapper.hidden = true;
        }
      }

      modalBackdrop.hidden = false;
      isModalOpen = true;
      setTimeout(function () {
        modalEl.focus?.();
      }, 0);
    }

    function closeModal() {
      if (!modalBackdrop) return;
      modalBackdrop.hidden = true;
      isModalOpen = false;
    }

    function switchTab(activeChip) {
      const targetId = activeChip.getAttribute('aria-controls');
      if (!targetId) return;

      chips.forEach(function (chip) {
        const isActive = chip === activeChip;
        chip.classList.toggle('product-reviews-tabs__chip--active', isActive);
        chip.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach(function (panel) {
        const isTarget = panel.id === targetId;
        panel.classList.toggle('is-visible', isTarget);
        panel.hidden = !isTarget;

        if (isTarget) {
          const slider = panel.querySelector('[data-slider]');
          const dots = panel.querySelector('[data-dots]');
          if (slider) slider.scrollLeft = 0;
          if (dots) {
            const dotBtns = dots.querySelectorAll('.product-reviews-tabs__dot');
            dotBtns.forEach(function (d, i) {
              d.classList.toggle('product-reviews-tabs__dot--active', i === 0);
            });
          }
        }
      });
    }

    chipList?.addEventListener('click', function (e) {
      const chip = e.target.closest('[role="tab"]');
      if (chip) switchTab(chip);
    });

    sectionEl.addEventListener('click', function (e) {
      const readMoreBtn = e.target.closest('[data-review-read-more]');
      if (readMoreBtn && sectionEl.contains(readMoreBtn)) {
        openModalFromButton(readMoreBtn);
      }
    });

    modalBackdrop?.addEventListener('click', function (e) {
      if (e.target === modalBackdrop) {
        closeModal();
      }
    });

    modalCloseBtn?.addEventListener('click', function () {
      closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (!isModalOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        closeModal();
      }
    });

    panels.forEach(function (panel) {
      const slider = panel.querySelector('[data-slider]');
      const dots = panel.querySelector('[data-dots]');
      const prevBtn = panel.querySelector('[data-slider-prev]');
      const nextBtn = panel.querySelector('[data-slider-next]');

      if (!slider) return;

      // Detect truncated cards and toggle gradient indicator
      const cardTexts = panel.querySelectorAll('.product-reviews-tabs__card-text[data-review-text]');
      cardTexts.forEach(function (el) {
        const body = el.closest('.product-reviews-tabs__card');
        if (!body) return;
        requestAnimationFrame(function () {
          if (el.scrollHeight <= el.clientHeight + 2) {
            body.classList.add('product-reviews-tabs__card--not-truncated');
          }
        });
      });

      var isDown = false;
      var startX;
      var scrollLeftStart;

      slider.addEventListener('mousedown', function (e) {
        isDown = true;
        startX = e.pageX - slider.offsetLeft;
        scrollLeftStart = slider.scrollLeft;
        slider.classList.add('is-dragging');
      });

      slider.addEventListener('mouseleave', function () {
        isDown = false;
        slider.classList.remove('is-dragging');
      });

      slider.addEventListener('mouseup', function () {
        isDown = false;
        slider.classList.remove('is-dragging');
      });

      slider.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var x = e.pageX - slider.offsetLeft;
        var walk = (x - startX) * 1.5;
        slider.scrollLeft = scrollLeftStart - walk;
      });

      const cardsPerPage = window.innerWidth >= 750 ? 3 : 1;
      const cardCount = slider.children.length;
      const pageCount = Math.max(1, Math.ceil(cardCount / cardsPerPage));

      function updateDots() {
        if (!dots) return;
        const dotBtns = dots.querySelectorAll('.product-reviews-tabs__dot');
        if (dotBtns.length === 0) return;
        const cardWidth = slider.children[0]?.offsetWidth || 0;
        const gap = parseFloat(getComputedStyle(slider).gap) || 16;
        const step = cardWidth + gap;
        const currentPage = Math.min(
          pageCount - 1,
          Math.round(slider.scrollLeft / (slider.offsetWidth || 1))
        );
        dotBtns.forEach(function (d, i) {
          d.classList.toggle('product-reviews-tabs__dot--active', i === currentPage);
        });
      }

      slider.addEventListener('scroll', updateDots, { passive: true });

      dots?.addEventListener('click', function (e) {
        const dot = e.target.closest('.product-reviews-tabs__dot');
        if (!dot) return;
        const index = parseInt(dot.dataset.dotIndex, 10);
        if (isNaN(index)) return;
        const cardWidth = slider.children[0]?.offsetWidth || 0;
        const gap = parseFloat(getComputedStyle(slider).gap) || 16;
        const step = cardWidth + gap;
        const pageWidth = slider.offsetWidth;
        const targetScroll = Math.min(index * pageWidth, slider.scrollWidth - pageWidth);
        slider.scrollTo({ left: targetScroll, behavior: 'smooth' });
      });

      prevBtn?.addEventListener('click', function () {
        slider.scrollBy({ left: -slider.offsetWidth, behavior: 'smooth' });
      });

      nextBtn?.addEventListener('click', function () {
        slider.scrollBy({ left: slider.offsetWidth, behavior: 'smooth' });
      });
    });
  }

  function initAll() {
    document.querySelectorAll('.product-reviews-tabs[data-section-id]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    const section = (e.target?.classList?.contains('product-reviews-tabs') ? e.target : null)
      || (e.detail?.sectionId ? document.getElementById('shopify-section-' + e.detail.sectionId) : null);
    if (section) {
      delete section.dataset.productReviewsTabsInit;
      initSection(section);
    }
  });
})();
